import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  ArrowLeft, 
  Save, 
  RefreshCw, 
  Download, 
  FileEdit, 
  Sparkles,
  CheckCircle,
  AlertCircle,
  MessageCircle,
  Eye,
  EyeOff,
  TrendingUp,
  Target,
  Star
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useFamilyContext } from '../../contexts/FamilyContext';
import EssayMarkupView from './EssayRefinement/EssayMarkupView';
import AnalysisLoader from './EssayRefinement/AnalysisLoader';
import EnhancedOverallAssessment from './EssayRefinement/EnhancedOverallAssessment';
import FirstSentenceAnalysis from './EssayRefinement/FirstSentenceAnalysis';
import LastSentenceAnalysis from './EssayRefinement/LastSentenceAnalysis';
import DimensionScoreBreakdown from './EssayRefinement/DimensionScoreBreakdown';
import CoachingEngine from '../../utils/CoachingEngine';
import FeedbackValidator from '../../utils/FeedbackValidator';
import WritingSupportSidebar from './WritingSupport/WritingSupportSidebar';

// Helper function to find best match for text in content
// Prioritizes matches near the expected position
const findBestMatch = (content, searchText, expectedPosition) => {
  if (!content || !searchText) return -1;
  
  // First try exact match at expected position
  if (content.slice(expectedPosition, expectedPosition + searchText.length) === searchText) {
    return expectedPosition;
  }
  
  // Find all occurrences
  const occurrences = [];
  let index = content.indexOf(searchText);
  while (index !== -1) {
    occurrences.push(index);
    index = content.indexOf(searchText, index + 1);
  }
  
  if (occurrences.length === 0) return -1;
  if (occurrences.length === 1) return occurrences[0];
  
  // Find closest to expected position
  let closest = occurrences[0];
  let minDistance = Math.abs(occurrences[0] - expectedPosition);
  
  for (const pos of occurrences) {
    const distance = Math.abs(pos - expectedPosition);
    if (distance < minDistance) {
      minDistance = distance;
      closest = pos;
    }
  }
  
  return closest;
};

// Helper function for fuzzy matching when exact match fails
const findFuzzyMatch = (content, searchText) => {
  if (!content || !searchText) return -1;
  
  // Try matching with normalized whitespace
  const normalizedSearch = searchText.replace(/\s+/g, ' ').trim();
  const normalizedContent = content.replace(/\s+/g, ' ');
  
  const index = normalizedContent.indexOf(normalizedSearch);
  if (index !== -1) {
    // Map back to original content position
    let originalIndex = 0;
    let normalizedIndex = 0;
    
    while (normalizedIndex < index && originalIndex < content.length) {
      if (content[originalIndex].match(/\s/) && normalizedContent[normalizedIndex] === ' ') {
        // Skip whitespace in original until we match normalized
        while (originalIndex < content.length && content[originalIndex].match(/\s/)) {
          originalIndex++;
        }
        normalizedIndex++;
      } else if (content[originalIndex] === normalizedContent[normalizedIndex]) {
        originalIndex++;
        normalizedIndex++;
      } else {
        originalIndex++;
      }
    }
    
    return {
      start: originalIndex,
      end: originalIndex + searchText.length
    };
  }
  
  // Try partial match (first 50% of text)
  const partialSearch = searchText.substring(0, Math.floor(searchText.length * 0.5));
  const partialIndex = content.indexOf(partialSearch);
  
  if (partialIndex !== -1) {
    // Verify the rest matches reasonably well
    const endIndex = partialIndex + searchText.length;
    const extractedText = content.substring(partialIndex, endIndex);
    
    // Calculate similarity
    let matches = 0;
    for (let i = 0; i < Math.min(extractedText.length, searchText.length); i++) {
      if (extractedText[i] === searchText[i]) matches++;
    }
    
    const similarity = matches / searchText.length;
    if (similarity > 0.7) { // 70% similarity threshold
      return {
        start: partialIndex,
        end: endIndex
      };
    }
  }
  
  return -1;
};

// Helper function to find all occurrences of text
const findAllOccurrences = (content, searchText) => {
  if (!content || !searchText) return [];
  
  const occurrences = [];
  let index = content.indexOf(searchText);
  while (index !== -1) {
    occurrences.push(index);
    index = content.indexOf(searchText, index + 1);
  }
  
  return occurrences;
};

// Helper function for smart text truncation that respects word boundaries
const smartTruncate = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  
  const truncated = text.substring(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(' ');
  
  // If there's a space within the last 10 characters, cut at the word boundary
  if (lastSpaceIndex > maxLength - 10) {
    return truncated.substring(0, lastSpaceIndex);
  }
  
  // Otherwise, just truncate at the character limit
  return truncated;
};

// Helper function to calculate text change percentage
const calculateChangePercentage = (originalText, newText) => {
  if (!originalText || !newText) return 100;
  if (originalText === newText) return 0;
  
  // Simple approach: calculate percentage of different characters
  const maxLength = Math.max(originalText.length, newText.length);
  let differences = 0;
  
  for (let i = 0; i < maxLength; i++) {
    if (originalText[i] !== newText[i]) {
      differences++;
    }
  }
  
  return Math.round((differences / maxLength) * 100);
};

// Helper function to determine if re-analysis should be allowed
const shouldAllowReanalysis = (currentContent, lastAnalyzedContent, reanalysisCount, changePercentage) => {
  // Always allow first analysis
  if (reanalysisCount === 0) return true;
  
  // Allow one immediate re-analysis regardless of changes
  if (reanalysisCount === 1) return true;
  
  // After that, require significant changes (>20%)
  return changePercentage >= 20;
};

const EssayRefinementInterface = ({
  initialEssayContent = '',
  essayTitle = '',
  essayId = null,
  preSelectedEssay = null,
  slotAttachedContent = {},
  onNavigateBack = null,
  onSwitchToDraft = null,
  onSwitchToBuilder = null,
  onSaveAnalysis = null,
  onLoadAnalysis = null,
  onSaveDraft = null
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useFamilyContext();
  
  // Get essay data from props (hub mode) or route state (standalone mode)
  const routeEssayData = preSelectedEssay || location.state?.essay;
  const workingTitle = preSelectedEssay?.title || essayTitle || routeEssayData?.title || 'Untitled Essay';
  const initialContent = preSelectedEssay?.content || initialEssayContent || routeEssayData?.content || '';
  const effectiveEssayId = preSelectedEssay?.id || essayId || routeEssayData?.id || null;
  
  // Core state
  const [workingDraft, setWorkingDraft] = useState(initialContent);
  const [originalDraft, setOriginalDraft] = useState(initialContent);
  const [feedback, setFeedback] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStage, setAnalysisStage] = useState('analyzing');
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [analysisVersion, setAnalysisVersion] = useState(0);

  // Analysis tracking state
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [lastAnalyzedContent, setLastAnalyzedContent] = useState(initialContent);
  const [reanalysisCount, setReanalysisCount] = useState(0);
  const [canReanalyze, setCanReanalyze] = useState(true);
  const [changePercentage, setChangePercentage] = useState(0);
  
  // UI State (removed showCoachComments since we always show the markup now)
  const [refinementLevel, setRefinementLevel] = useState('standard'); // standard, detailed, comprehensive
  const [wordCount, setWordCount] = useState(0);
  const [closedCallouts, setClosedCallouts] = useState(new Set());
  const [lastSaved, setLastSaved] = useState(null);
  const [showWritingSupport, setShowWritingSupport] = useState(false);
  
  // Refs
  const draftTextareaRef = useRef(null);
  const analysisTimeoutRef = useRef(null);
  const feedbackPanelRef = useRef(null);
  
  // Load saved analysis function
  const loadSavedAnalysis = useCallback(async (essayId) => {
    if (!essayId) return null;

    // Allow parent to override persistence
    if (onLoadAnalysis) {
      try {
        return await onLoadAnalysis(essayId);
      } catch (error) {
        console.error('Error loading analysis via callback:', error);
        return null;
      }
    }

    try {
      const { data, error } = await supabase
        .from('college_journey_essays')
        .select('refinement_analysis, has_analysis, analysis_version, last_analyzed_at, analysis_word_count')
        .eq('id', essayId)
        .single();

      if (error) {
        console.error('Error loading saved analysis:', error);
        return null;
      }

      if (data?.has_analysis && data?.refinement_analysis) {
        console.log('Loaded saved analysis for essay:', essayId);
        return {
          analysis: data.refinement_analysis,
          version: data.analysis_version,
          lastAnalyzedAt: data.last_analyzed_at,
          wordCount: data.analysis_word_count
        };
      }

      return null;
    } catch (error) {
      console.error('Error loading analysis from database:', error);
      return null;
    }
  }, [onLoadAnalysis]);

  // Save analysis to database
  const saveAnalysisToDatabase = useCallback(async (essayId, analysisData, essayContent) => {
    const wordCount = essayContent.trim().split(/\s+/).filter(w => w.length > 0).length;

    // Allow parent to override persistence
    if (onSaveAnalysis) {
      try {
        await onSaveAnalysis(essayId, {
          feedback: analysisData,
          analysisVersion: reanalysisCount + 1,
          lastAnalyzedAt: new Date().toISOString(),
          analysisWordCount: wordCount
        });
        console.log('Analysis saved via callback for essay:', essayId);
        return;
      } catch (error) {
        console.error('Error saving analysis via callback:', error);
        toast.error('Failed to save analysis');
        return;
      }
    }

    try {
      const { error } = await supabase
        .from('college_journey_essays')
        .update({
          refinement_analysis: analysisData,
          has_analysis: true,
          analysis_version: (reanalysisCount + 1),
          last_analyzed_at: new Date().toISOString(),
          analysis_word_count: wordCount,
          updated_at: new Date().toISOString()
        })
        .eq('id', essayId);

      if (error) {
        console.error('Error saving analysis:', error);
        toast.error('Failed to save analysis');
      } else {
        console.log('Analysis saved successfully for essay:', essayId);
      }
    } catch (error) {
      console.error('Error saving analysis to database:', error);
    }
  }, [reanalysisCount, onSaveAnalysis]);

  // Function to request missing sentence analyses separately
  const requestMissingSentenceAnalyses = useCallback(async (currentFeedback, essayContent) => {
    try {
      const requests = [];
      
      // Request first sentence analysis if missing
      if (currentFeedback.firstSentenceAnalysis?.needsSeparateRequest) {
        console.log('📝 Requesting first sentence analysis separately...');
        requests.push(
          fetch('/api/anthropic/analysis', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'sentence-analysis',
              type: 'sentence-analysis',
              sentenceType: 'first',
              essayContent,
              essayTitle: essayTitle || 'Essay'
            })
          })
        );
      }
      
      // Request last sentence analysis if missing
      if (currentFeedback.lastSentenceAnalysis?.needsSeparateRequest) {
        console.log('📝 Requesting last sentence analysis separately...');
        requests.push(
          fetch('/api/anthropic/analysis', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'sentence-analysis',
              type: 'sentence-analysis',
              sentenceType: 'last',
              essayContent,
              essayTitle: essayTitle || 'Essay'
            })
          })
        );
      }
      
      if (requests.length === 0) return;
      
      // Execute requests in parallel
      const responses = await Promise.allSettled(requests);
      const updatedFeedback = { ...currentFeedback };
      
      for (let i = 0; i < responses.length; i++) {
        const response = responses[i];
        if (response.status === 'fulfilled' && response.value.ok) {
          const data = await response.value.json();
          
          if (data.sentenceType === 'first' && data.analysis) {
            console.log('✅ Received first sentence analysis:', data.analysis);
            updatedFeedback.firstSentenceAnalysis = data.analysis;
          } else if (data.sentenceType === 'last' && data.analysis) {
            console.log('✅ Received last sentence analysis:', data.analysis);
            updatedFeedback.lastSentenceAnalysis = data.analysis;
          }
        } else {
          console.error('❌ Failed to get sentence analysis:', response.reason || 'Unknown error');
        }
      }
      
      // Update feedback with the new analyses
      setFeedback(updatedFeedback);
      
      // Save updated analysis to database
      if (effectiveEssayId) {
        saveAnalysisToDatabase(effectiveEssayId, updatedFeedback, essayContent);
      }
      
    } catch (error) {
      console.error('Error requesting sentence analyses:', error);
      // Don't show error to user - these are supplementary analyses
    }
  }, [effectiveEssayId, saveAnalysisToDatabase, essayTitle]);

  // Initialize and load saved analysis
  useEffect(() => {
    const initializeEssay = async () => {
      if (initialContent) {
        setWorkingDraft(initialContent);
        setOriginalDraft(initialContent);
        updateWordCount(initialContent);
        
        // Try to load saved analysis first
        if (effectiveEssayId) {
          const savedAnalysis = await loadSavedAnalysis(effectiveEssayId);
          
          if (savedAnalysis) {
            // Load the saved analysis
            setFeedback(savedAnalysis.analysis);
            setAnalysisVersion(savedAnalysis.version || 1);
            setReanalysisCount(savedAnalysis.version || 1);
            setLastAnalyzedContent(initialContent);
            
            // Check if content has changed significantly since last analysis
            const currentWordCount = initialContent.trim().split(/\s+/).filter(w => w.length > 0).length;
            const wordCountDiff = Math.abs(currentWordCount - (savedAnalysis.wordCount || 0));
            const significantChange = wordCountDiff > currentWordCount * 0.2; // >20% change
            
            if (significantChange) {
              toast.info('Your essay has changed significantly since the last analysis. Consider re-analyzing.');
            }
          } else if (initialContent.length >= 100) {
            // No saved analysis, auto-trigger new analysis
            setTimeout(() => {
              triggerAnalysis(initialContent);
            }, 100);
          }
        } else if (initialContent.length >= 100) {
          // No essay ID, just trigger analysis
          setTimeout(() => {
            triggerAnalysis(initialContent);
          }, 100);
        }
      }
    };
    
    initializeEssay();
  }, [initialContent, effectiveEssayId]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Update word count
  const updateWordCount = useCallback((text) => {
    const words = text.trim().split(/\s+/).filter(w => w.length > 0).length;
    setWordCount(words);
  }, []);

  // Track changes and update re-analysis eligibility
  useEffect(() => {
    if (workingDraft && lastAnalyzedContent) {
      const changePercent = calculateChangePercentage(lastAnalyzedContent, workingDraft);
      setChangePercentage(changePercent);
      
      const canReanalyzeNow = shouldAllowReanalysis(workingDraft, lastAnalyzedContent, reanalysisCount, changePercent);
      setCanReanalyze(canReanalyzeNow);
      
      console.log('Change tracking:', {
        changePercentage: changePercent,
        reanalysisCount,
        canReanalyze: canReanalyzeNow,
        contentLength: workingDraft.length,
        lastAnalyzedLength: lastAnalyzedContent.length
      });
    }
  }, [workingDraft, lastAnalyzedContent, reanalysisCount]);
  
  // Handle draft content changes
  const handleDraftChange = (e) => {
    const newContent = e.target.value;
    setWorkingDraft(newContent);
    setHasUnsavedChanges(true);
    updateWordCount(newContent);
    
    // Auto-analysis disabled - users must manually trigger analysis
    // Clear any existing timeout to prevent unwanted analysis
    if (analysisTimeoutRef.current) {
      clearTimeout(analysisTimeoutRef.current);
    }
  };
  
  // Enhanced triggerAnalysis with history and warnings
  const triggerAnalysis = async (content = workingDraft, forceAnalysis = false) => {
    if (!content?.trim() || content.length < 100) return;
    
    // Check if re-analysis is allowed
    if (!forceAnalysis && !canReanalyze) {
      const changeNeeded = 20 - changePercentage;
      toast.error(`Make more substantial changes (${changeNeeded.toFixed(0)}% more) before re-analyzing`, {
        duration: 4000
      });
      return;
    }
    
    // Show warning for subsequent analyses
    if (reanalysisCount > 0 && !forceAnalysis) {
      const proceed = window.confirm(
        `This will overwrite your current analysis. You have ${changePercentage}% changes since last analysis. Continue?`
      );
      if (!proceed) return;
    }
    
    setIsAnalyzing(true);
    setAnalysisStage('analyzing');
    
    // Progress through stages
    const stageTimeout = setTimeout(() => setAnalysisStage('understanding'), 3000);
    const stageTimeout2 = setTimeout(() => setAnalysisStage('identifying'), 7000);
    const stageTimeout3 = setTimeout(() => setAnalysisStage('crafting'), 12000);
    const stageTimeout4 = setTimeout(() => setAnalysisStage('finalizing'), 18000);
    const stageTimeout5 = setTimeout(() => setAnalysisStage('processing'), 25000);
    
    // Set a 60-second timeout with warning
    const warningTimeout = setTimeout(() => {
      toast('Analysis is taking longer than usual. Please wait...', {
        icon: '⏳',
        duration: 4000
      });
    }, 45000);
    
    const absoluteTimeout = setTimeout(() => {
      setIsAnalyzing(false);
      toast.error('Analysis timed out. Please try again.', {
        duration: 5000
      });
    }, 60000);
    
    try {
      // First, run rule-based analysis for immediate feedback
      const ruleBasedFeedback = CoachingEngine.analyzeText(content);
      
      // Set immediate feedback
      setFeedback({
        ...ruleBasedFeedback,
        isProcessing: true
      });
      
      // Get student profile for context
      const studentProfile = await getStudentProfile();
      
      // Prepare previous analysis context for consistency
      const previousAnalysisContext = analysisHistory.length > 0 ? {
        previousScore: analysisHistory[analysisHistory.length - 1].overallScore,
        previousStrengths: analysisHistory[analysisHistory.length - 1].strengths || [],
        previousWeaknesses: analysisHistory[analysisHistory.length - 1].weaknesses || [],
        analysisCount: reanalysisCount + 1,
        changePercentage
      } : null;
      
      // Call AI analysis API
      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'refine-essay',
          essayContent: content,
          refinementLevel,
          studentProfile,
          ruleBasedFeedback,
          essayTitle: workingTitle,
          previousAnalysisContext // Include context for consistency
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Analysis failed: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        // Store the cleaned essay content if provided (for accurate index mapping)
        const cleanedEssayContent = result.cleanedEssayContent || content;
        
        // Parse the JSON from the analysis field if it exists
        let parsedFeedback = result.feedback;
        
        // Check both analysis and coachComments fields (API returns in coachComments)
        const jsonSource = result.feedback?.analysis || result.feedback?.coachComments;
        
        if (jsonSource) {
          try {
            // Remove markdown code block formatting if present
            let cleanJson = jsonSource
              .replace(/```json\n?/g, '')
              .replace(/```\n?/g, '')
              .trim();
            
            // Robust JSON parsing with error recovery
            let analysisData;
            try {
              analysisData = JSON.parse(cleanJson);
              console.log('✅ Successfully parsed full JSON response, sections present:', {
                hasAnnotations: !!analysisData.annotations,
                hasOverallScore: !!analysisData.overallScore,
                hasFirstSentence: !!analysisData.firstSentenceAnalysis,
                hasLastSentence: !!analysisData.lastSentenceAnalysis,
                hasPatternAnalysis: !!analysisData.patternAnalysis,
                hasDimensionScores: !!analysisData.dimensionScores
              });
            } catch (initialParseError) {
              console.warn('Initial JSON parse failed, attempting repair:', initialParseError.message);
              
              // More comprehensive JSON repair for common issues
              try {
                // First attempt: Fix common string issues including the \"* problem
                let repairedJson = cleanJson
                  // First fix the specific \"* issue in the essay text
                  .replace(/\\"\\*/g, '\\\\"*')
                  // Fix newlines within strings (replace with escaped newlines)
                  .replace(/("(?:[^"\\]|\\.)*?")/g, (match) => {
                    return match.replace(/\n/g, '\\n').replace(/\r/g, '\\r');
                  })
                  // Fix missing commas between array elements
                  .replace(/\}\s*\{/g, '},{')
                  .replace(/\]\s*\[/g, '],[')
                  .replace(/("\s*)\s*(")/g, '$1,$2')
                  .replace(/(\d)\s*(")/g, '$1,$2')
                  .replace(/(")\s*(\d)/g, '$1,$2')
                  // Fix missing commas after properties
                  .replace(/("\s*:\s*"[^"]*")\s*(")/g, '$1,$2')
                  .replace(/("\s*:\s*\d+)\s*(")/g, '$1,$2')
                  .replace(/("\s*:\s*(?:true|false|null))\s*(")/g, '$1,$2')
                  // Fix trailing commas
                  .replace(/,(\s*[}\]])/g, '$1')
                  // Fix unescaped quotes within strings (conservative approach)
                  .replace(/"([^"]*)"(\s*[,}\]])/g, (match, content, after) => {
                    const fixed = content.replace(/"/g, '\\"');
                    return `"${fixed}"${after}`;
                  });
                
                analysisData = JSON.parse(repairedJson);
                console.log('JSON repair successful');
              } catch (repairError) {
                console.error('Advanced repair failed, trying structural extraction:', repairError.message);
                
                // Second attempt: Extract JSON structure piece by piece
                try {
                  // Initialize variables at the top scope
                  let lineByLineComments = [];
                  let overallAssessment = {};
                  let strategicInsights = {};
                  let coachVoice = null;
                  
                  // First check for new character-indexed format
                  const annotationsMatch = cleanJson.match(/"annotations"\s*:\s*\[([^\]]*(?:\[[^\]]*\][^\]]*)*)\]/s);
                  const overallScoreMatch = cleanJson.match(/"overallScore"\s*:\s*(\d+)/);
                  const overallFeedbackMatch = cleanJson.match(/"overallFeedback"\s*:\s*"([^"]*)"/);
                  
                  if (annotationsMatch && overallScoreMatch) {
                    // New format detected - extract annotations
                    let annotations = [];
                    const annotationPattern = /\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g;
                    let annotationMatch;
                    while ((annotationMatch = annotationPattern.exec(annotationsMatch[1])) !== null) {
                      try {
                        const annotation = JSON.parse('{' + annotationMatch[1] + '}');
                        annotations.push(annotation);
                      } catch (e) {
                        // Skip malformed annotation
                      }
                    }
                    
                    // Parse patternAnalysis from the response
                    let patternAnalysis = {};
                    try {
                      const patternStart = cleanJson.indexOf('"patternAnalysis"');
                      if (patternStart !== -1) {
                        const colonIndex = cleanJson.indexOf(':', patternStart);
                        const openBraceIndex = cleanJson.indexOf('{', colonIndex);
                        
                        if (openBraceIndex !== -1) {
                          let braceCount = 0;
                          let closeBraceIndex = openBraceIndex;
                          
                          for (let i = openBraceIndex; i < cleanJson.length; i++) {
                            if (cleanJson[i] === '{') braceCount++;
                            if (cleanJson[i] === '}') {
                              braceCount--;
                              if (braceCount === 0) {
                                closeBraceIndex = i;
                                break;
                              }
                            }
                          }
                          
                          const patternAnalysisJson = cleanJson.substring(openBraceIndex, closeBraceIndex + 1);
                          patternAnalysis = JSON.parse(patternAnalysisJson);
                          console.log('Successfully parsed patternAnalysis:', patternAnalysis);
                        }
                      }
                    } catch (e) {
                      console.warn('Failed to parse patternAnalysis, using fallback:', e.message);
                      patternAnalysis = {};
                    }

                    analysisData = {
                      annotations: annotations,
                      overallScore: parseInt(overallScoreMatch[1]) || 0,
                      overallFeedback: overallFeedbackMatch ? overallFeedbackMatch[1] : '',
                      patternAnalysis: patternAnalysis,
                      lineByLineComments: [], // Empty for compatibility
                      overallAssessment: {},
                      strategicInsights: {},
                      actionableImprovements: [],
                      coachVoice: null
                    };
                    
                    console.log('Extracted character-indexed format with', annotations.length, 'annotations');
                  } else {
                    // Fall back to old format extraction
                    // Extract overallAssessment
                    const assessmentMatch = cleanJson.match(/"overallAssessment"\s*:\s*\{([^}]+(?:\{[^}]*\}[^}]*)*)\}/);
                    let overallAssessment = {};
                    if (assessmentMatch) {
                      try {
                        overallAssessment = JSON.parse('{' + assessmentMatch[1] + '}');
                      } catch (e) {
                        // Extract individual fields
                        overallAssessment = {
                          uniquenessScore: parseInt(cleanJson.match(/"uniquenessScore"\s*:\s*(\d+)/)?.[1]) || 0,
                          authenticityScore: parseInt(cleanJson.match(/"authenticityScore"\s*:\s*(\d+)/)?.[1]) || 0,
                          impactScore: parseInt(cleanJson.match(/"impactScore"\s*:\s*(\d+)/)?.[1]) || 0,
                          strengths: [],
                          weaknesses: []
                        };
                      }
                    }
                    
                    // Extract lineByLineComments array more carefully
                    let lineByLineComments = [];
                    const commentsMatch = cleanJson.match(/"lineByLineComments"\s*:\s*\[([^\]]*(?:\[[^\]]*\][^\]]*)*)\]/s);
                    if (commentsMatch) {
                    // Try to extract individual comment objects
                    const commentPattern = /\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g;
                    let commentMatch;
                    while ((commentMatch = commentPattern.exec(commentsMatch[1])) !== null) {
                      try {
                        const comment = JSON.parse('{' + commentMatch[1] + '}');
                        lineByLineComments.push(comment);
                      } catch (e) {
                        // Skip malformed comment
                      }
                    }
                  }
                  
                  // Extract strategicInsights with more robust parsing
                  let strategicInsights = {};
                  try {
                    // First try to find and extract the entire strategicInsights object
                    const insightsStart = cleanJson.indexOf('"strategicInsights"');
                    if (insightsStart !== -1) {
                      // Find the opening brace after the colon
                      const colonIndex = cleanJson.indexOf(':', insightsStart);
                      const openBraceIndex = cleanJson.indexOf('{', colonIndex);
                      
                      if (openBraceIndex !== -1) {
                        // Find the matching closing brace
                        let braceCount = 0;
                        let closeBraceIndex = openBraceIndex;
                        
                        for (let i = openBraceIndex; i < cleanJson.length; i++) {
                          if (cleanJson[i] === '{') braceCount++;
                          if (cleanJson[i] === '}') {
                            braceCount--;
                            if (braceCount === 0) {
                              closeBraceIndex = i;
                              break;
                            }
                          }
                        }
                        
                        const strategicInsightsJson = cleanJson.substring(openBraceIndex, closeBraceIndex + 1);
                        strategicInsights = JSON.parse(strategicInsightsJson);
                        console.log('Successfully parsed strategicInsights:', strategicInsights);
                      }
                    }
                  } catch (e) {
                    console.warn('Failed to parse strategicInsights, using fallback:', e.message);
                    // Fallback: try the original regex method
                    const insightsMatch = cleanJson.match(/"strategicInsights"\s*:\s*\{([^}]+(?:\{[^}]*\}[^}]*)*)\}/);
                    if (insightsMatch) {
                      try {
                        strategicInsights = JSON.parse('{' + insightsMatch[1] + '}');
                      } catch (e2) {
                        strategicInsights = { 
                          mainTheme: 'Strategic analysis unavailable', 
                          missedOpportunities: [], 
                          strengthsToAmplify: [],
                          standoutMoments: [],
                          collegePositioning: ''
                        };
                      }
                    } else {
                      strategicInsights = { 
                        mainTheme: 'Strategic analysis unavailable', 
                        missedOpportunities: [], 
                        strengthsToAmplify: [],
                        standoutMoments: [],
                        collegePositioning: ''
                      };
                    }
                  }
                  
                  // Parse first sentence analysis with fallback generation
                  let firstSentenceAnalysis = null;
                  try {
                    const firstSentenceStart = cleanJson.indexOf('"firstSentenceAnalysis"');
                    console.log('🔍 Parsing firstSentenceAnalysis:', {
                      startIndex: firstSentenceStart,
                      foundSection: firstSentenceStart !== -1,
                      jsonLength: cleanJson.length
                    });
                    
                    if (firstSentenceStart !== -1) {
                      const colonIndex = cleanJson.indexOf(':', firstSentenceStart);
                      const openBraceIndex = cleanJson.indexOf('{', colonIndex);
                      
                      if (openBraceIndex !== -1) {
                        let braceCount = 0;
                        let closeBraceIndex = openBraceIndex;
                        
                        for (let i = openBraceIndex; i < cleanJson.length; i++) {
                          if (cleanJson[i] === '{') braceCount++;
                          if (cleanJson[i] === '}') {
                            braceCount--;
                            if (braceCount === 0) {
                              closeBraceIndex = i;
                              break;
                            }
                          }
                        }
                        
                        const firstSentenceJson = cleanJson.substring(openBraceIndex, closeBraceIndex + 1);
                        console.log('📝 Extracted firstSentence JSON:', {
                          extractedLength: firstSentenceJson.length,
                          preview: firstSentenceJson.substring(0, 100) + '...'
                        });
                        
                        firstSentenceAnalysis = JSON.parse(firstSentenceJson);
                        console.log('✅ Successfully parsed firstSentenceAnalysis:', firstSentenceAnalysis);
                      } else {
                        console.error('❌ Found firstSentenceAnalysis key but no opening brace');
                      }
                    } else {
                      console.error('❌ firstSentenceAnalysis not found in response - will request separate analysis');
                      // Mark that we need to request this separately
                      firstSentenceAnalysis = { needsSeparateRequest: true };
                    }
                  } catch (e) {
                    console.error('❌ Failed to parse firstSentenceAnalysis:', e.message);
                    console.log('Failed JSON excerpt:', cleanJson.substring(
                      Math.max(0, firstSentenceStart - 50),
                      Math.min(cleanJson.length, firstSentenceStart + 200)
                    ));
                    // Mark that we need to request this separately
                    firstSentenceAnalysis = { needsSeparateRequest: true };
                  }

                  // Parse last sentence analysis with fallback generation
                  let lastSentenceAnalysis = null;
                  try {
                    const lastSentenceStart = cleanJson.indexOf('"lastSentenceAnalysis"');
                    console.log('🔍 Parsing lastSentenceAnalysis:', {
                      startIndex: lastSentenceStart,
                      foundSection: lastSentenceStart !== -1,
                      jsonLength: cleanJson.length
                    });
                    
                    if (lastSentenceStart !== -1) {
                      const colonIndex = cleanJson.indexOf(':', lastSentenceStart);
                      const openBraceIndex = cleanJson.indexOf('{', colonIndex);
                      
                      if (openBraceIndex !== -1) {
                        let braceCount = 0;
                        let closeBraceIndex = openBraceIndex;
                        
                        for (let i = openBraceIndex; i < cleanJson.length; i++) {
                          if (cleanJson[i] === '{') braceCount++;
                          if (cleanJson[i] === '}') {
                            braceCount--;
                            if (braceCount === 0) {
                              closeBraceIndex = i;
                              break;
                            }
                          }
                        }
                        
                        const lastSentenceJson = cleanJson.substring(openBraceIndex, closeBraceIndex + 1);
                        console.log('📝 Extracted lastSentence JSON:', {
                          extractedLength: lastSentenceJson.length,
                          preview: lastSentenceJson.substring(0, 100) + '...'
                        });
                        
                        lastSentenceAnalysis = JSON.parse(lastSentenceJson);
                        console.log('✅ Successfully parsed lastSentenceAnalysis:', lastSentenceAnalysis);
                      } else {
                        console.error('❌ Found lastSentenceAnalysis key but no opening brace');
                      }
                    } else {
                      console.error('❌ lastSentenceAnalysis not found in response - will request separate analysis');
                      // Mark that we need to request this separately
                      lastSentenceAnalysis = { needsSeparateRequest: true };
                    }
                  } catch (e) {
                    console.error('❌ Failed to parse lastSentenceAnalysis:', e.message);
                    console.log('Failed JSON excerpt:', cleanJson.substring(
                      Math.max(0, lastSentenceStart - 50),
                      Math.min(cleanJson.length, lastSentenceStart + 200)
                    ));
                    // Mark that we need to request this separately
                    lastSentenceAnalysis = { needsSeparateRequest: true };
                  }
                  
                  // Extract coachVoice
                  const coachMatch = cleanJson.match(/"coachVoice"\s*:\s*"([^"]*)"/);
                  const coachVoice = coachMatch ? coachMatch[1] : null;
                  
                  analysisData = {
                    overallAssessment,
                    lineByLineComments,
                    strategicInsights,
                    firstSentenceAnalysis,
                    lastSentenceAnalysis,
                    actionableImprovements: [],
                    coachVoice
                  };
                  }
                  
                  console.log('Structural extraction completed with', lineByLineComments.length, 'comments');
                } catch (extractError) {
                  console.error('Structural extraction failed:', extractError.message);
                  
                  // Final fallback: minimal data structure
                  analysisData = {
                    overallAssessment: {
                      uniquenessScore: 5,
                      authenticityScore: 5,
                      impactScore: 5,
                      strengths: ['Unable to parse full feedback - showing partial results'],
                      weaknesses: ['Please try analyzing again for complete feedback']
                    },
                    lineByLineComments: [],
                    strategicInsights: {
                      mainTheme: 'Analysis partially available',
                      missedOpportunities: [],
                      strengthsToAmplify: []
                    },
                    actionableImprovements: [],
                    coachVoice: 'Due to a parsing error, only partial feedback is available. Please try analyzing your essay again.'
                  };
                }
              }
            }
            
            // Check if this is the new character-indexed format
            if (analysisData.annotations && Array.isArray(analysisData.annotations)) {
              console.log('Detected character-indexed format with', analysisData.annotations.length, 'annotations');
              
              // Debug logging for text comparison
              console.log('Text comparison:', {
                originalLength: content.length,
                cleanedLength: cleanedEssayContent.length,
                workingDraftLength: workingDraft.length,
                originalFirst100: content.substring(0, 100),
                cleanedFirst100: cleanedEssayContent.substring(0, 100),
                workingFirst100: workingDraft.substring(0, 100)
              });
              
              // Enhanced validation with detailed debugging
              const validatedAnnotations = analysisData.annotations.map((ann, index) => {
                console.log(`\n🔍 Processing annotation ${ann.id || index + 1}:`);
                console.log(`  Original text: "${ann.originalText}"`);
                console.log(`  AI indices: [${ann.startIndex}, ${ann.endIndex}]`);
                
                // First check if indices match in the cleaned content (what the AI analyzed)
                const extractedFromCleaned = cleanedEssayContent.slice(ann.startIndex, ann.endIndex);
                console.log(`  From cleaned: "${extractedFromCleaned}"`);
                console.log(`  Match with cleaned: ${extractedFromCleaned === ann.originalText}`);
                
                if (extractedFromCleaned === ann.originalText) {
                  // Perfect match in cleaned content - now map to working draft
                  
                  // Strategy 1: Try direct mapping (most common case)
                  const directExtract = content.slice(ann.startIndex, ann.endIndex);
                  if (directExtract === ann.originalText) {
                    console.log(`  ✅ Direct mapping works`);
                    return {
                      ...ann,
                      mappingStrategy: 'direct'
                    };
                  }
                  
                  // Strategy 2: Use smart finding with position hints
                  const bestMatch = findBestMatch(content, ann.originalText, ann.startIndex);
                  if (bestMatch !== -1) {
                    const verifyExtract = content.slice(bestMatch, bestMatch + ann.originalText.length);
                    if (verifyExtract === ann.originalText) {
                      console.log(`  ✅ Best match found at ${bestMatch} (offset: ${bestMatch - ann.startIndex})`);
                      return {
                        ...ann,
                        startIndex: bestMatch,
                        endIndex: bestMatch + ann.originalText.length,
                        mappingStrategy: 'bestMatch',
                        offset: bestMatch - ann.startIndex
                      };
                    }
                  }
                  
                  console.log(`  ⚠️ Cleaned match but can't map to working draft`);
                } else {
                  // Indices don't match even in cleaned content - this suggests AI error
                  console.warn(`  ❌ AI index error for annotation ${ann.id}: expected "${ann.originalText}", got "${extractedFromCleaned}"`);
                  
                  // Strategy 3: Search in both versions
                  const allOccurrencesInContent = findAllOccurrences(content, ann.originalText);
                  const allOccurrencesInCleaned = findAllOccurrences(cleanedEssayContent, ann.originalText);
                  
                  console.log(`  Found ${allOccurrencesInContent.length} occurrences in working draft`);
                  console.log(`  Found ${allOccurrencesInCleaned.length} occurrences in cleaned content`);
                  
                  if (allOccurrencesInContent.length > 0) {
                    // Pick the occurrence closest to AI's suggested position
                    const closestMatch = allOccurrencesInContent.reduce((closest, current) => {
                      return Math.abs(current - ann.startIndex) < Math.abs(closest - ann.startIndex) ? current : closest;
                    });
                    
                    console.log(`  ✅ Using closest match at ${closestMatch}`);
                    return {
                      ...ann,
                      startIndex: closestMatch,
                      endIndex: closestMatch + ann.originalText.length,
                      mappingStrategy: 'closestMatch',
                      corrected: true
                    };
                  }
                  
                  // Strategy 4: Try fuzzy matching as last resort
                  const fuzzyResult = findFuzzyMatch(content, ann.originalText);
                  if (fuzzyResult && fuzzyResult !== -1) {
                    console.log(`  ⚠️ Using fuzzy match at [${fuzzyResult.start}, ${fuzzyResult.end}]`);
                    return {
                      ...ann,
                      startIndex: fuzzyResult.start,
                      endIndex: fuzzyResult.end,
                      mappingStrategy: 'fuzzy',
                      fuzzyMatched: true
                    };
                  }
                }
                
                // Ultimate fallback: return original annotation with warning
                console.error(`  💥 Could not map annotation ${ann.id} to working draft - using original indices`);
                return {
                  ...ann,
                  mappingStrategy: 'failed',
                  mappingFailed: true
                };
              });
              
              parsedFeedback = {
                // Direct mapping for character-indexed format with validated annotations
                annotations: validatedAnnotations,
                overallScore: analysisData.overallScore,
                overallFeedback: analysisData.overallFeedback,
                patternAnalysis: analysisData.patternAnalysis,
                
                // Add dimension scores and authenticity data
                dimensionScores: analysisData.dimensionScores,
                authenticityCheck: analysisData.authenticityCheck,
                memorabilityAssessment: analysisData.memorabilityAssessment,
                
                // Add sentence analysis data
                firstSentenceAnalysis: analysisData.firstSentenceAnalysis,
                lastSentenceAnalysis: analysisData.lastSentenceAnalysis,
                
                // Map to lineByLineComments for backward compatibility
                lineByLineComments: validatedAnnotations,
                
                // Include original essay for reference
                originalEssay: analysisData.originalEssay || content,
                cleanedEssay: cleanedEssayContent, // Include cleaned essay for accurate rendering
                
                // Extract and include coach voice and strategic insights
                coachVoice: analysisData.coachVoice || analysisData.coachFeedback || 
                           'Click highlighted text to view detailed feedback. Remember that AI can make mistakes, so be sure to pay honor your own voice, knowledge, and experience.',
                
                strategicInsights: analysisData.strategicInsights || {
                  mainTheme: analysisData.overallFeedback ? 
                    `Your essay effectively ${analysisData.overallScore >= 80 ? 'showcases' : 'begins to explore'} your unique perspective` : 
                    'Analysis in progress',
                  strengthsToAmplify: analysisData.strengths || [],
                  missedOpportunities: analysisData.improvements || [],
                  standoutMoments: validatedAnnotations
                    .filter(a => a.severity === 'minor' && a.category === 'content')
                    .map(a => smartTruncate(a.originalText) + '...'),
                  weakestParagraph: validatedAnnotations
                    .find(a => a.severity === 'major')?.feedback || null
                },
                
                // Preserve existing fields for compatibility
                overallAssessment: {
                  uniquenessScore: Math.round((analysisData.overallScore || 0) / 10),
                  authenticityScore: Math.round((analysisData.overallScore || 0) / 10),
                  impactScore: Math.round((analysisData.overallScore || 0) / 10),
                  summary: analysisData.overallFeedback || 'Analysis complete - see annotations for detailed feedback'
                },
                
                // Metadata
                isProcessing: false,
                analysisId: result.analysisId,
                timestamp: new Date().toISOString(),
                metadata: {
                  ...result.metadata,
                  formatType: 'character-indexed',
                  totalAnnotations: analysisData.annotations.length
                }
              };
            } else {
              // Fall back to old format handling
              // Map the parsed data to the structure expected by FeedbackRenderer
              // Enhanced feedback parsing with validation and enrichment
              const enhancedComments = (analysisData.lineByLineComments || []).map((comment, index) => {
              return {
                ...comment,
                id: `comment-${index}`, // Add unique ID for React keys
                paragraphNumber: comment.paragraphNumber || null,
                context: comment.context || null,
                matchQuality: null, // Will be set by MarkupRenderer
                // Validate required fields
                originalText: comment.originalText || '',
                comment: comment.comment || 'No comment provided',
                severity: ['low', 'medium', 'high'].includes(comment.severity) ? comment.severity : 'medium',
                type: ['strength', 'improvement', 'question', 'critical'].includes(comment.type) ? comment.type : 'improvement'
              };
            });

            parsedFeedback = {
              // Enhanced core assessment data
              overallAssessment: {
                ...analysisData.overallAssessment,
                // Ensure scores are numbers
                uniquenessScore: Number(analysisData.overallAssessment?.uniquenessScore) || 0,
                authenticityScore: Number(analysisData.overallAssessment?.authenticityScore) || 0,
                impactScore: Number(analysisData.overallAssessment?.impactScore) || 0,
                // Add enhanced fields
                firstImpressionImpact: analysisData.overallAssessment?.firstImpressionImpact || null,
                memorabilityFactor: analysisData.overallAssessment?.memorabilityFactor || null
              },
              
              lineByLineComments: enhancedComments,
              
              strategicInsights: analysisData.strategicInsights ? {
                ...analysisData.strategicInsights,
                // Add enhanced fields with defaults
                standoutMoments: analysisData.strategicInsights.standoutMoments || [],
                weakestParagraph: analysisData.strategicInsights.weakestParagraph || null
              } : {
                // Use the default strategic insights from earlier
                mainTheme: parsedFeedback.strategicInsights?.mainTheme || 'Analysis in progress',
                strengthsToAmplify: parsedFeedback.strategicInsights?.strengthsToAmplify || [],
                missedOpportunities: parsedFeedback.strategicInsights?.missedOpportunities || [],
                standoutMoments: parsedFeedback.strategicInsights?.standoutMoments || [],
                collegePositioning: parsedFeedback.strategicInsights?.collegePositioning || '',
                weakestParagraph: null
              },
              
              // Add sentence analysis data
              firstSentenceAnalysis: analysisData.firstSentenceAnalysis,
              lastSentenceAnalysis: analysisData.lastSentenceAnalysis,
              
              actionableImprovements: (analysisData.actionableImprovements || []).map((improvement, index) => ({
                ...improvement,
                id: `improvement-${index}`,
                beforeAfterExample: improvement.beforeAfterExample || null,
                category: improvement.category || 'general'
              })),
              
              coachVoice: analysisData.coachVoice || null,
              
              // Legacy compatibility
              overallScore: analysisData.overallScore || null,
              suggestions: analysisData.suggestions || result.feedback.suggestions || [],
              strengths: analysisData.strengths || result.feedback.strengths || [],
              improvements: analysisData.improvements || result.feedback.improvements || [],
              issues: analysisData.issues || result.feedback.issues || [],
              
              // Preserve any rule-based feedback
              ...ruleBasedFeedback,
              
              // Enhanced metadata
              isProcessing: false,
              analysisId: result.analysisId,
              timestamp: new Date().toISOString(),
              metadata: {
                ...result.metadata,
                enhancedFeedback: true,
                totalComments: enhancedComments.length,
                commentTypes: enhancedComments.reduce((acc, comment) => {
                  acc[comment.type] = (acc[comment.type] || 0) + 1;
                  return acc;
                }, {}),
                improvementCategories: (analysisData.actionableImprovements || []).reduce((acc, improvement) => {
                  acc[improvement.category || 'general'] = (acc[improvement.category || 'general'] || 0) + 1;
                  return acc;
                }, {})
              }
            };
            } // End of else block for old format
          } catch (parseError) {
            console.error('Error parsing feedback analysis:', parseError);
            // Fallback to original feedback structure
            parsedFeedback = {
              ...result.feedback,
              ...ruleBasedFeedback,
              isProcessing: false,
              analysisId: result.analysisId,
              timestamp: new Date().toISOString(),
              parseError: true
            };
            toast.error('Some feedback details could not be parsed');
          }
        }
        
        // Debug: Log the strategic insights before setting feedback
        console.log('📊 Strategic Insights in parsedFeedback:', parsedFeedback.strategicInsights);
        console.log('📊 Full parsedFeedback structure:', {
          hasStrategicInsights: !!parsedFeedback.strategicInsights,
          strategicInsightsKeys: parsedFeedback.strategicInsights ? Object.keys(parsedFeedback.strategicInsights) : [],
          mainTheme: parsedFeedback.strategicInsights?.mainTheme,
          standoutMoments: parsedFeedback.strategicInsights?.standoutMoments,
          strengthsToAmplify: parsedFeedback.strategicInsights?.strengthsToAmplify,
          missedOpportunities: parsedFeedback.strategicInsights?.missedOpportunities,
          collegePositioning: parsedFeedback.strategicInsights?.collegePositioning
        });
        
        // Validate feedback quality before setting
        if (parsedFeedback.annotations && Array.isArray(parsedFeedback.annotations)) {
          console.log('🔍 Validating feedback quality...');
          const validationResults = FeedbackValidator.validateAllAnnotations(
            parsedFeedback.annotations,
            content
          );
          
          // Log validation results
          console.log('✅ Validation Results:', validationResults.summary);
          
          if (validationResults.problematic.length > 0) {
            console.warn('⚠️ Problematic annotations detected:', validationResults.problematic);
            
            // Generate quality report
            const qualityReport = FeedbackValidator.generateQualityReport(validationResults);
            console.log('📋 Quality Report:', qualityReport);
            
            // Optionally show warning to user if quality is low
            if (validationResults.summary.avgQuality < 70) {
              toast.warning('Some feedback may need manual review for accuracy');
            }
          }
          
          // Optional: Filter out low-quality annotations
          const qualityThreshold = 50; // Annotations with quality score below this are filtered
          const filteredAnnotations = validationResults.allResults
            .filter(result => result.qualityScore >= qualityThreshold)
            .map(result => result.annotation);
          
          // Update parsedFeedback with filtered annotations if filtering is enabled
          const enableFiltering = false; // Toggle this to enable/disable filtering
          if (enableFiltering && filteredAnnotations.length < parsedFeedback.annotations.length) {
            console.log(`🎯 Filtered ${parsedFeedback.annotations.length - filteredAnnotations.length} low-quality annotations`);
            parsedFeedback.annotations = filteredAnnotations;
          }
        }
        
        setFeedback(parsedFeedback);
        setAnalysisVersion(prev => prev + 1);
        
        // Check if we need to request sentence analyses separately
        if (parsedFeedback.firstSentenceAnalysis?.needsSeparateRequest || 
            parsedFeedback.lastSentenceAnalysis?.needsSeparateRequest) {
          console.log('🔄 Requesting missing sentence analyses separately...');
          requestMissingSentenceAnalyses(parsedFeedback, content);
        }
        
        // Update analysis tracking
        const analysisRecord = {
          id: result.analysisId,
          timestamp: new Date().toISOString(),
          content: content,
          contentLength: content.length,
          wordCount: content.trim().split(/\s+/).filter(w => w.length > 0).length,
          overallScore: parsedFeedback.overallScore || analysisData?.overallScore,
          strengths: parsedFeedback.strengths || [],
          weaknesses: parsedFeedback.improvements || [],
          changePercentage: changePercentage,
          analysisVersion: reanalysisCount + 1
        };
        
        setAnalysisHistory(prev => [...prev, analysisRecord]);
        setLastAnalyzedContent(content);
        setReanalysisCount(prev => prev + 1);
        setChangePercentage(0); // Reset change tracking
        
        // Save analysis to database if we have an essay ID
        if (effectiveEssayId) {
          saveAnalysisToDatabase(effectiveEssayId, parsedFeedback, content);
        }
        
        // Set stage to processing while feedback renders
        setAnalysisStage('processing');
        
        // Delay turning off the loader to ensure smooth transition
        setTimeout(() => {
          setIsAnalyzing(false);
        }, 500);
        
        // Success message with context
        if (reanalysisCount > 0) {
          toast.success(`Analysis updated (version ${reanalysisCount + 1})`, {
            duration: 3000
          });
        } else {
          toast.success('Essay analyzed successfully', {
            duration: 3000
          });
        }
      } else {
        throw new Error(result.error || 'Analysis failed');
      }
      
    } catch (error) {
      console.error('Error analyzing essay:', error);
      toast.error('Analysis failed. Try again in a moment.');
      
      // Keep rule-based feedback even if AI analysis fails
      setFeedback(prev => ({
        ...prev,
        isProcessing: false,
        aiAnalysisFailed: true,
        error: error.message
      }));
      
      // Still turn off analyzing on error
      setIsAnalyzing(false);
    } finally {
      // Clear all timeouts
      clearTimeout(stageTimeout);
      clearTimeout(stageTimeout2);
      clearTimeout(stageTimeout3);
      clearTimeout(stageTimeout4);
      clearTimeout(stageTimeout5);
      clearTimeout(warningTimeout);
      clearTimeout(absoluteTimeout);
    }
  };
  
  // Get student profile for personalized coaching
  const getStudentProfile = async () => {
    if (!currentUser?.id) return null;
    
    try {
      const { data, error } = await supabase
        .from('student_academic_profiles')
        .select('*')
        .eq('user_id', currentUser.id)
        .single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.warn('Could not fetch student profile:', error);
      return null;
    }
  };
  
  // Save draft
  const saveDraft = async () => {
    if (!workingDraft.trim()) return;

    setIsSaving(true);
    try {
      // Allow parent to override persistence
      if (onSaveDraft) {
        await onSaveDraft(effectiveEssayId, workingDraft, wordCount);
        setHasUnsavedChanges(false);
        setLastSaved(new Date());
        toast.success('Draft saved successfully');
        return;
      }

      const saveData = {
        title: workingTitle,
        content: workingDraft,
        word_count: wordCount,
        status: 'draft',
        last_refined: new Date().toISOString(),
        refinement_feedback: feedback
      };

      let result;
      if (essayId) {
        // Update existing essay
        result = await supabase
          .from('college_journey_essays')
          .update(saveData)
          .eq('id', essayId)
          .select()
          .single();
      } else {
        // Create new essay
        result = await supabase
          .from('college_journey_essays')
          .insert([{
            ...saveData,
            user_id: currentUser.id,
            created_at: new Date().toISOString()
          }])
          .select()
          .single();
      }

      if (result.error) throw result.error;

      setHasUnsavedChanges(false);
      setLastSaved(new Date());
      toast.success('Draft saved successfully');

    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Export essay with feedback
  const exportEssay = () => {
    const exportData = {
      title: workingTitle,
      content: workingDraft,
      wordCount,
      feedback: feedback,
      exportedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${workingTitle.replace(/[^a-zA-Z0-9]/g, '_')}_refined.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Essay exported with feedback');
  };
  
  // Apply suggestion from feedback
  const applySuggestion = (suggestionId, newText, originalText) => {
    const updatedContent = workingDraft.replace(originalText, newText);
    setWorkingDraft(updatedContent);
    setHasUnsavedChanges(true);
    updateWordCount(updatedContent);
    
    toast.success('Suggestion applied');
  };
  
  // Format last saved time
  const formatLastSaved = (date) => {
    if (!date) return '';
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just saved';
    if (minutes < 60) return `Saved ${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Saved ${hours}h ago`;
    
    return `Saved ${date.toLocaleDateString()}`;
  };

  return (
    <div className="h-screen bg-stone-50 flex">
      {/* Main content area */}
      <div className="flex-1 flex flex-col">
        {/* Header - Clean Swiss Typography Style */}
        <div className="bg-white border-b border-stone-200">
          <div className="px-6 py-4">
            {/* Top row: Title + Actions */}
            <div className="flex items-start justify-between mb-3">
              {/* Left: Title Section */}
              <div className="flex items-start gap-3">
                {!onNavigateBack ? (
                  <button
                    onClick={() => navigate(-1)}
                    className="p-1 hover:bg-stone-100 rounded transition-colors mt-0.5"
                    aria-label="Go back"
                  >
                    <ArrowLeft className="w-4 h-4 text-stone-500" />
                  </button>
                ) : null}
                <div>
                  <h1 className="text-base font-bold text-slate-900 leading-tight">{workingTitle}</h1>
                  <p className="text-xs text-stone-500 mt-0.5">Essay Refinement</p>
                </div>
              </div>
              
              {/* Right: Primary Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowWritingSupport(!showWritingSupport)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all text-xs font-medium border ${
                    showWritingSupport
                      ? 'bg-indigo-500 text-white border-slate-900'
                      : 'bg-white text-slate-700 border-slate-900 hover:bg-stone-50'
                  }`}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Notes
                </button>

                <button
                  onClick={exportEssay}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors text-xs font-medium"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export
                </button>
                
                <button
                  onClick={saveDraft}
                  disabled={isSaving || !hasUnsavedChanges}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
                >
                  {isSaving ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Save className="w-3.5 h-3.5" />
                  )}
                  Save
                </button>
              </div>
            </div>

            {/* Bottom row: Metrics + Re-analyze */}
            <div className="flex items-center justify-between">
              {/* Left: Score + Metrics */}
              <div className="flex items-center gap-4">
                {/* Essay Score - Big Number */}
                {feedback?.overallScore && (
                  <div className="flex items-center gap-2">
                    <div className={`text-2xl font-bold tabular-nums ${
                      feedback.overallScore >= 80 ? 'text-emerald-600' :
                      feedback.overallScore >= 60 ? 'text-gold-600' : 'text-coral-500'
                    }`}>
                      {feedback.overallScore}
                    </div>
                    <span className="text-xs text-stone-500">Essay Score</span>
                  </div>
                )}
                
                {/* Dimension Scores - Minimal with colored dots */}
                {feedback?.overallAssessment && (
                  <div className="flex items-center gap-3 pl-4 border-l border-stone-200">
                    {feedback.overallAssessment.uniquenessScore && (
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                        <span className="text-xs text-stone-600">Unique</span>
                        <span className="text-xs font-bold text-slate-900 tabular-nums">{feedback.overallAssessment.uniquenessScore}/10</span>
                      </div>
                    )}
                    {feedback.overallAssessment.authenticityScore && (
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-teal-500"></div>
                        <span className="text-xs text-stone-600">Authentic</span>
                        <span className="text-xs font-bold text-slate-900 tabular-nums">{feedback.overallAssessment.authenticityScore}/10</span>
                      </div>
                    )}
                    {feedback.overallAssessment.impactScore && (
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-azure-500"></div>
                        <span className="text-xs text-stone-600">Impact</span>
                        <span className="text-xs font-bold text-slate-900 tabular-nums">{feedback.overallAssessment.impactScore}/10</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Word count */}
                <div className="pl-4 border-l border-stone-200">
                  <span className="text-xs text-stone-500">{wordCount} words</span>
                </div>
              </div>

              {/* Right: Re-analyze + Status */}
              <div className="flex items-center gap-4">
                {/* Last saved indicator */}
                {lastSaved && (
                  <div className="text-xs text-stone-400">
                    {formatLastSaved(lastSaved)}
                  </div>
                )}
                
                {/* Re-analyze button with change percentage */}
                <div className="flex items-center gap-2">
                  {reanalysisCount > 0 && (
                    <div className="text-xs text-stone-500 tabular-nums">
                      {changePercentage}% changed • {analysisHistory.length} version{analysisHistory.length !== 1 ? 's' : ''}
                    </div>
                  )}
                  <button
                    onClick={() => triggerAnalysis()}
                    disabled={isAnalyzing || !canReanalyze}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all text-xs font-medium border ${
                      canReanalyze 
                        ? 'bg-white text-slate-700 border-slate-900 hover:bg-stone-50' 
                        : 'bg-stone-50 text-stone-400 border-stone-200 cursor-not-allowed'
                    } disabled:opacity-50`}
                    title={
                      !canReanalyze 
                        ? `Make ${(20 - changePercentage).toFixed(0)}% more changes to re-analyze` 
                        : reanalysisCount > 0 
                          ? 'This will overwrite current analysis' 
                          : 'Analyze your essay'
                    }
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isAnalyzing ? 'animate-spin' : ''}`} />
                    {reanalysisCount === 0 ? 'Analyze' : `Re-analyze${reanalysisCount > 0 ? ` (v${reanalysisCount + 1})` : ''}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      
      {/* Main Content - 50/50 Split */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Column: Editable Working Draft */}
        <div className="flex-1 bg-white flex flex-col border-r border-stone-200">
          <div className="bg-stone-50 border-b border-stone-200 px-6 py-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-stone-700 flex items-center gap-2">
                <FileEdit className="w-4 h-4" />
                Your Working Draft
              </h2>
              <div className="text-xs text-stone-500">
                Edit and refine based on coach feedback
              </div>
            </div>
          </div>
          
          <div className="flex-1 p-6 overflow-y-auto">
            <textarea
              ref={draftTextareaRef}
              value={workingDraft}
              onChange={handleDraftChange}
              placeholder="Your essay draft will appear here for editing..."
              className="w-full h-full p-4 border border-stone-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent font-serif text-md leading-relaxed"
              style={{
                minHeight: 'calc(100vh - 200px)'
              }}
            />
          </div>
        </div>
        
        {/* Right Column: Teacher Markup View */}
        <div className="flex-1 bg-gradient-to-br from-stone-50 to-stone-100 flex flex-col">
          <div className="bg-white border-b border-stone-200 px-6 py-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-stone-700 flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                Coach Feedback
              </h2>
              {isAnalyzing && (
                <div className="flex items-center gap-2 text-xs text-violet-600">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Analyzing...
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto relative">
            {/* Coach Voice Callout Box */}
            {feedback?.coachVoice && !closedCallouts.has('coachVoice') && (
              <div className="absolute top-4 right-4 left-4 z-10">
                <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 shadow-lg"
                     style={{fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif'}}>
                  <div className="flex items-start gap-2">
                    <MessageCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-xs font-semibold text-yellow-800 mb-1">Coach Says:</h3>
                      <p className="text-sm text-yellow-700 leading-relaxed">{feedback.coachVoice}</p>
                    </div>
                    <button 
                      onClick={() => setClosedCallouts(prev => new Set(prev).add('coachVoice'))}
                      className="ml-2 p-1 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-100 rounded transition-colors"
                      title="Close"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Main Markup Display */}
            <div className={`p-6 ${feedback?.coachVoice && !closedCallouts.has('coachVoice') ? 'pt-32' : ''}`}>
              {feedback && (feedback.annotations || feedback.lineByLineComments) ? (
                <EssayMarkupView
                  essayText={workingDraft} // Always use working draft since indices are mapped to it
                  markupData={{
                    annotations: feedback.annotations || feedback.lineByLineComments,
                    patternAnalysis: feedback.patternAnalysis,
                    overallScore: feedback.overallScore,
                    overallFeedback: feedback.overallFeedback || feedback.overallAssessment?.summary
                  }}
                />
              ) : (
                <div className="text-center py-12">
                  {(isAnalyzing || feedback?.isProcessing || 
                   (!feedback && workingDraft?.length >= 100 && analysisVersion > 0)) ? (
                    <AnalysisLoader stage={analysisStage} />
                  ) : (
                    <>
                      <MessageCircle className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                      <p className="text-stone-500">
                        {workingDraft && workingDraft.length >= 100 ? 'Click "Analyze Essay" to get feedback' : 
                         'Start writing to see coach feedback'}
                      </p>
                    </>
                  )}
                </div>
              )}
              
              {/* First Sentence Analysis */}
              <FirstSentenceAnalysis 
                firstSentenceData={feedback?.firstSentenceAnalysis}
                essayText={workingDraft}
              />
              
              {/* Dimension Score Breakdown - NEW */}
              {feedback?.dimensionScores && (
                <div className="mt-8">
                  <DimensionScoreBreakdown 
                    dimensionScores={feedback.dimensionScores}
                    authenticityCheck={feedback.authenticityCheck}
                    memorabilityAssessment={feedback.memorabilityAssessment}
                  />
                </div>
              )}
              
              {/* Enhanced Overall Assessment */}
              {feedback?.overallAssessment && (
                <div className="mt-8">
                  <EnhancedOverallAssessment 
                    overallAssessment={feedback.overallAssessment}
                    annotations={feedback.annotations || feedback.lineByLineComments || []}
                    essayText={workingDraft}
                  />
                </div>
              )}

              {/* Enhanced Strategic Insights */}
              {(() => {
                // Debug logging to see what we have
                if (feedback?.strategicInsights) {
                  console.log('🎯 Strategic Insights found:', {
                    hasData: true,
                    mainTheme: feedback.strategicInsights.mainTheme,
                    standoutMoments: feedback.strategicInsights.standoutMoments,
                    strengthsToAmplify: feedback.strategicInsights.strengthsToAmplify,
                    missedOpportunities: feedback.strategicInsights.missedOpportunities,
                    collegePositioning: feedback.strategicInsights.collegePositioning
                  });
                } else {
                  console.log('❌ No Strategic Insights found in feedback');
                }
                
                return feedback?.strategicInsights ? (
                  <div className="mt-8 bg-violet-50 border border-violet-300 rounded-lg p-4"
                       style={{fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif'}}>
                    <div className="flex items-start gap-2">
                      <Star className="w-4 h-4 text-violet-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h3 className="text-xs font-semibold text-violet-800 mb-2">Strategic Insights</h3>
                      
                      {feedback.strategicInsights.mainTheme && (
                        <p className="text-sm text-violet-700 mb-2">
                          <strong>Main Theme:</strong> {feedback.strategicInsights.mainTheme}
                        </p>
                      )}
                      
                      {feedback.strategicInsights.standoutMoments && feedback.strategicInsights.standoutMoments.length > 0 && (
                        <div className="mb-2">
                          <p className="text-xs font-semibold text-violet-700 mb-1">Standout Moments:</p>
                          <ul className="text-sm text-violet-600 space-y-1">
                            {feedback.strategicInsights.standoutMoments.map((moment, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <span className="text-violet-400">✨</span>
                                <span>{moment}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {feedback.strategicInsights.strengthsToAmplify && feedback.strategicInsights.strengthsToAmplify.length > 0 && (
                        <div className="mb-2">
                          <p className="text-xs font-semibold text-violet-700 mb-1">Amplify These Strengths:</p>
                          <ul className="text-sm text-violet-600 space-y-1">
                            {feedback.strategicInsights.strengthsToAmplify.map((strength, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <span className="text-violet-400">•</span>
                                <span>{strength}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {feedback.strategicInsights.missedOpportunities && feedback.strategicInsights.missedOpportunities.length > 0 && (
                        <div className="mb-2">
                          <p className="text-xs font-semibold text-violet-700 mb-1">Missed Opportunities:</p>
                          <ul className="text-sm text-violet-600 space-y-1">
                            {feedback.strategicInsights.missedOpportunities.map((opportunity, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <span className="text-violet-400">💡</span>
                                <span>{opportunity}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {feedback.strategicInsights.collegePositioning && (
                        <div>
                          <p className="text-xs font-semibold text-violet-700 mb-1">College Positioning:</p>
                          <p className="text-sm text-violet-600">{feedback.strategicInsights.collegePositioning}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                // Fallback when no strategic insights
                <div className="mt-8 bg-stone-50 border border-stone-300 rounded-lg p-4"
                     style={{fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif'}}>
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-xs font-semibold text-slate-600 mb-2">Strategic Insights</h3>
                      <p className="text-sm text-stone-500 italic">
                        Strategic analysis will appear here after essay processing completes.
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}
              
              {/* Enhanced Actionable Improvements Sidebar */}
              {feedback?.actionableImprovements && feedback.actionableImprovements.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Priority Improvements
                  </h3>
                  <div className="space-y-3">
                    {feedback.actionableImprovements.slice(0, 4).map((improvement, index) => (
                      <div key={index} className="bg-white border border-stone-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className={`w-2 h-2 rounded-full mt-1.5 ${
                            improvement.priority === 'high' ? 'bg-red-500' :
                            improvement.priority === 'medium' ? 'bg-orange-500' : 'bg-yellow-500'
                          }`} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="text-sm font-medium text-stone-800">{improvement.description}</h4>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                improvement.priority === 'high' ? 'bg-red-100 text-red-700' :
                                improvement.priority === 'medium' ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {improvement.priority}
                              </span>
                            </div>
                            <p className="text-xs text-stone-600 mb-2">{improvement.howTo}</p>
                            
                            {/* Before/After Example */}
                            {improvement.beforeAfterExample && (
                              <div className="mt-3 p-3 bg-stone-50 rounded border">
                                <div className="text-xs font-medium text-stone-700 mb-2">Example:</div>
                                <div className="text-xs text-stone-600 leading-relaxed">
                                  {improvement.beforeAfterExample}
                                </div>
                              </div>
                            )}
                            
                            {/* Category badge */}
                            {improvement.category && (
                              <span className="inline-block mt-2 px-2 py-1 bg-stone-100 text-stone-600 rounded text-xs">
                                {improvement.category}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Last Sentence Analysis */}
              <LastSentenceAnalysis
                lastSentenceData={feedback?.lastSentenceAnalysis}
                essayText={workingDraft}
              />

            </div>
          </div>
        </div>
        {/* End of right column */}
      </div>
      </div>
      {/* End of main content area */}
      
      {/* Writing Support Sidebar */}
      {showWritingSupport && (
        <WritingSupportSidebar
          currentMode="essays"
          selectedDocument={preSelectedEssay || { id: effectiveEssayId, title: workingTitle, content: workingDraft, slot_ids: preSelectedEssay?.slot_ids }}
          attachedFreewrites={slotAttachedContent.attachedFreewrites}
          attachedIdeas={slotAttachedContent.attachedIdeas}
          collegeInsights={slotAttachedContent.collegeInsights}
          onClose={() => setShowWritingSupport(false)}
          className="w-80 border-l border-stone-200"
        />
      )}
    </div>
  );
};

export default EssayRefinementInterface;